import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { safeJson } from '../utils/safeJson';
import { getHeadSink, type HeadDoc } from '../prerender/headSink';

export interface SEOProps {
    title?: string;
    /** Pass explicitly — no default so layout-level SEO doesn't override pages */
    description?: string;
    canonical?: string;
    noindex?: boolean;
    nofollow?: boolean;
    ogType?: 'website' | 'article' | 'profile';
    ogImage?: string;
    jsonLd?: object;
    /** Disable hreflang tags (for non-translatable pages like auth) */
    noHreflang?: boolean;
}

const DEFAULT_TITLE = 'Bricolat';
const SITE_URL = 'https://www.bricolat.co.ma';

const HREFLANGS = [
    { hreflang: 'fr-MA', prefix: '' },
    { hreflang: 'ar-MA', prefix: '/ar' },
    { hreflang: 'en-MA', prefix: '/en' },
    { hreflang: 'ar-Latn-MA', prefix: '/darija' },
    { hreflang: 'ar-Arab-MA', prefix: '/darija-ar' },
] as const;

const OG_LOCALES = {
    fr: 'fr_MA',
    ar: 'ar_MA',
    en: 'en_MA',
    darija: 'fr_MA',
    darija_ar: 'ar_MA',
} as const;

// P1: head helpers take the target document so the build can run them against a sink.
type HeadTarget = Document | HeadDoc;

const removeHeadNodes = (doc: HeadTarget, selector: string) => {
    doc.head.querySelectorAll(selector).forEach((node) => node.remove());
};

// Encodes a path exactly once, whether it arrives literal ("canapé") or already escaped ("canap%C3%A9").
export const encodePath = (path: string) => {
    try {
        return encodeURI(decodeURI(path));
    } catch {
        return encodeURI(path);
    }
};

const setMeta = (doc: HeadTarget, selector: string, attrs: Record<string, string>) => {
    removeHeadNodes(doc, selector);
    const meta = doc.createElement('meta');
    Object.entries(attrs).forEach(([key, value]) => meta.setAttribute(key, value));
    doc.head.appendChild(meta as never);
};

const setLink = (doc: HeadTarget, selector: string, attrs: Record<string, string>) => {
    removeHeadNodes(doc, selector);
    const link = doc.createElement('link');
    Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value));
    doc.head.appendChild(link as never);
};

/**
 * SEO component that upserts head tags instead of rendering duplicate tags
 * during client hydration. React-snap still captures the resulting head for
 * static HTML, and the browser keeps one canonical set after route changes.
 */
export const SEO: React.FC<SEOProps> = ({
    title,
    description,
    canonical,
    noindex = false,
    nofollow = false,
    ogType = 'website',
    ogImage = '/og-image.png',
    jsonLd,
    noHreflang = false,
}) => {
    const { i18n } = useTranslation();
    const location = useLocation();

    const fullTitle = title
        ? (title.endsWith(` | ${DEFAULT_TITLE}`) ? title : `${title} | ${DEFAULT_TITLE}`)
        : null;
    // Never emit canonical/hreflang for noindex pages. It avoids conflicting
    // signals on auth/search/private flows and keeps indexable pages explicit.
    const emitCanonical = !noindex && (fullTitle !== null || !!canonical);
    // Build canonical URL: always www + https, strip trailing slash (except root "/").
    // Accepts both full URLs ("https://...") and relative paths ("/services/plomberie").
    const rawCanonicalPath = canonical
        ? (canonical.startsWith('http') ? new URL(canonical).pathname : canonical)
        : location.pathname;
    const pathLangMatch = location.pathname.match(/^\/(ar|en|darija|darija-ar)(?:\/|$)/);
    // SEO-R2: derive social locale from the crawl URL, not only client i18n state.
    const pathLanguage = pathLangMatch?.[1] === 'darija-ar' ? 'darija_ar' : pathLangMatch?.[1];
    const activeLanguage = pathLanguage === 'ar' || pathLanguage === 'en' || pathLanguage === 'darija' || pathLanguage === 'darija_ar'
        ? pathLanguage
        : i18n.language === 'ar' || i18n.language === 'en' || i18n.language === 'darija' || i18n.language === 'darija_ar'
            ? i18n.language
            : 'fr';
    const pathLangPrefix = pathLangMatch ? `/${pathLangMatch[1]}` : '';
    const canonicalPath = pathLangPrefix && !/^\/(ar|en|darija|darija-ar)(?:\/|$)/.test(rawCanonicalPath)
        ? `${pathLangPrefix}${rawCanonicalPath === '/' ? '' : rawCanonicalPath}`
        : rawCanonicalPath;
    const normalizedPath = canonicalPath === '/' ? '/' : canonicalPath.replace(/\/+$/, '');
    // SEO-R2: validator/prod sitemap compare encoded request paths verbatim; encode any
    // literal or already-escaped non-ASCII path segment consistently in canonical sets.
    // location.pathname is already percent-encoded: decode once before encodeURI, otherwise
    // "canap%C3%A9" would become "canap%25C3%25A9" (a URL that does not exist).
    const canonicalUrl = `${SITE_URL}${encodePath(normalizedPath)}`;
    const baseAlternatePath = normalizedPath.replace(/^\/(ar|en|darija|darija-ar)(?=\/|$)/, '') || '/';
    const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`;

    // Only emit robots meta when it differs from the crawlable default (index, follow).
    // Omitting it for the default case prevents layout-level <SEO> from colliding with
    // page-level noindex — two <meta name="robots"> in the DOM causes the first one to win,
    // which would silently suppress a page's noindex directive.
    const shouldEmitRobots = noindex || nofollow;
    const robotsContent = [
        noindex ? 'noindex' : 'index',
        nofollow ? 'nofollow' : 'follow',
    ].join(', ');
    const robotsOnly = !fullTitle && !description && !canonical && !jsonLd;

    const applyHead = (doc: HeadTarget) => {
        const clearPageHeadTags = () => {
            removeHeadNodes(doc, 'meta[name="description"]');
            removeHeadNodes(doc, 'link[rel="canonical"]');
            removeHeadNodes(doc, 'link[rel="alternate"]');
            removeHeadNodes(doc, 'meta[property^="og:"], meta[name^="twitter:"]');
            removeHeadNodes(doc, 'script[data-seo-jsonld="true"]');
        };

        if (robotsOnly) {
            clearPageHeadTags();
            removeHeadNodes(doc, 'meta[name="robots"]');
            if (shouldEmitRobots) {
                setMeta(doc, 'meta[name="robots"]', { name: 'robots', content: robotsContent });
            }
            return;
        }

        if (fullTitle) {
            doc.title = fullTitle;
        }

        clearPageHeadTags();
        if (description) {
            setMeta(doc, 'meta[name="description"]', { name: 'description', content: description });
        }

        removeHeadNodes(doc, 'meta[name="robots"]');
        if (shouldEmitRobots) {
            setMeta(doc, 'meta[name="robots"]', { name: 'robots', content: robotsContent });
        }

        if (emitCanonical) {
            setLink(doc, 'link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });
        }

        if (emitCanonical && !noHreflang) {
            HREFLANGS.forEach(({ hreflang, prefix }) => {
                const hrefPath = prefix ? `${prefix}${baseAlternatePath === '/' ? '' : baseAlternatePath}` : baseAlternatePath;
                const link = doc.createElement('link');
                link.setAttribute('rel', 'alternate');
                link.setAttribute('hrefLang', hreflang);
                link.setAttribute('href', `${SITE_URL}${encodePath(hrefPath)}`);
                doc.head.appendChild(link as never);
            });
            const xDefault = doc.createElement('link');
            xDefault.setAttribute('rel', 'alternate');
            xDefault.setAttribute('hrefLang', 'x-default');
            xDefault.setAttribute('href', `${SITE_URL}${encodePath(baseAlternatePath)}`);
            doc.head.appendChild(xDefault as never);
        }

        if (description) {
            setMeta(doc, 'meta[property="og:type"]', { property: 'og:type', content: ogType });
            if (fullTitle) setMeta(doc, 'meta[property="og:title"]', { property: 'og:title', content: fullTitle });
            setMeta(doc, 'meta[property="og:description"]', { property: 'og:description', content: description });
            setMeta(doc, 'meta[property="og:image"]', { property: 'og:image', content: ogImageUrl });
            setMeta(doc, 'meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' });
            setMeta(doc, 'meta[property="og:image:height"]', { property: 'og:image:height', content: '630' });
            setMeta(doc, 'meta[property="og:site_name"]', { property: 'og:site_name', content: DEFAULT_TITLE });
            setMeta(doc, 'meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
            setMeta(doc, 'meta[property="og:locale"]', { property: 'og:locale', content: OG_LOCALES[activeLanguage] });
            ([...new Set(Object.values(OG_LOCALES))].filter((locale) => locale !== OG_LOCALES[activeLanguage])).forEach((locale) => {
                const meta = doc.createElement('meta');
                meta.setAttribute('property', 'og:locale:alternate');
                meta.setAttribute('content', locale);
                doc.head.appendChild(meta as never);
            });
            setMeta(doc, 'meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
            if (fullTitle) setMeta(doc, 'meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
            setMeta(doc, 'meta[name="twitter:description"]', { name: 'twitter:description', content: description });
            setMeta(doc, 'meta[name="twitter:image"]', { name: 'twitter:image', content: ogImageUrl });
        }

        if (jsonLd) {
            const script = doc.createElement('script') as HTMLScriptElement;
            script.type = 'application/ld+json';
            script.dataset.seoJsonld = 'true';
            script.textContent = safeJson(jsonLd);
            doc.head.appendChild(script as never);
        }
    };

    // Server render (build): run the same code against the head sink, in tree order.
    const sink = typeof document === 'undefined' ? getHeadSink() : null;
    if (sink) applyHead(sink.doc);

    useEffect(() => {
        applyHead(document);
    }, [activeLanguage, baseAlternatePath, canonical, canonicalUrl, description, emitCanonical, fullTitle, i18n.language, jsonLd, noHreflang, ogImageUrl, ogType, robotsContent, robotsOnly, shouldEmitRobots]);

    return null;
};

export default SEO;
